import React, { FC, useState, useRef, useEffect, useMemo, cloneElement } from 'react';
import { Button } from '../../../../components/Button/Button';
import { useDebounce } from 'src/hooks/useDebounce';
import { Dropdown, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import '../../DashboardScreens.scss';
import { ReactComponent as ProgramIcon } from '../../../../../../assets/images/onboarding-trainer/dashboard/programs/program.svg';
import { ReactComponent as UploadIcon } from '../../../../../../assets/images/onboarding-trainer/dashboard/programs/upload.svg';
import { ReactComponent as ListIcon } from '../../../../../../assets/images/onboarding-trainer/dashboard/list.svg';
import { ReactComponent as MenuIcon } from '../../../../../../assets/images/onboarding-trainer/dashboard/menu.svg';
import { ReactComponent as AiIcon } from '../../../../../../assets/images/onboarding-trainer/dashboard/programs/ai.svg';
import { ReactComponent as AddPhotoFiles } from '../../../../../../assets/images/onboarding-trainer/dashboard/programs/add_photo_files.svg';
import { ReactComponent as TakeAPhotos } from '../../../../../../assets/images/onboarding-trainer/dashboard/programs/take_a_photo.svg';
import { ReactComponent as FilterIcon } from '../../../../../../assets/images/onboarding-trainer/dashboard/clients/filter.svg';
import { ReactComponent as SearchIcon } from '../../../../../../assets/images/onboarding-trainer/dashboard/clients/search.svg';
import { ProgramCard, ProgramData } from './components/ProgramCard/ProgramCard';

import { useGetTrainerProgramPlanList } from 'src/api/mutations/useGetTrainerProgramPlanList';
import { useGetTrainerClients } from 'src/api/mutations/useGetTrainerClients';
import { useGetAllMovements } from 'src/api/queries/useGetAllMovements';
import { isFemaleProfile } from 'src/utils/genderUtils';
import { sessionStore } from 'src/store/sessionStore';

import { useNavigate } from 'react-router-dom';

export const Programs: FC = () => {
  const navigate = useNavigate();
  const currentUser = sessionStore((s) => s.currentUser);
  const trainerId = currentUser?.uid;
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilter, setShowFilter] = useState('1'); // "1" is Active, "0" is Inactive
  const [clientFilter, setClientFilter] = useState('all');
  const [sortFilter, setSortFilter] = useState('newest');
  const [clientPage, setClientPage] = useState(1);
  const [allClients, setAllClients] = useState<any[]>([]);
  const [totalClientCount, setTotalClientCount] = useState(0);

  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const { data: programsData = [], isLoading: isProgramsLoading } = useGetTrainerProgramPlanList({
    isActive: showFilter === 'all' ? '' : showFilter,
    clientId: clientFilter === 'all' ? '' : clientFilter,
    sortFilter,
    searchQuery: debouncedSearchQuery,
  });
  const { data: activeProgramsData = [], isLoading: isActiveProgramsLoading } =
    useGetTrainerProgramPlanList({
      isActive: '1',
    });
  const { data: allMovements } = useGetAllMovements();
  const { data: clientsData, isFetching: isFetchingClients } = useGetTrainerClients(
    'all',
    '',
    'newest',
    clientPage,
    1000,
  );

  useEffect(() => {
    if (clientsData?.clients) {
      if (clientPage === 1) {
        setAllClients(clientsData.clients);
      } else {
        setAllClients((prev) => {
          const existingIds = new Set(prev.map((c) => c.uid));
          const newClients = clientsData.clients.filter((c) => !existingIds.has(c.uid));
          return [...prev, ...newClients];
        });
      }
      setTotalClientCount(clientsData.totalCount || 0);
    }
  }, [clientsData, clientPage]);

  const showItems = [
    { key: '1', label: 'Active' },
    { key: '0', label: 'Archive' },
    { key: 'all', label: 'All' },
  ];

  // Build client dropdown options dynamically from the fetched clients
  const clientItems: { key: string; label: React.ReactNode }[] = [
    { key: 'all', label: 'All Clients' },
    ...allClients.map((c) => ({
      key: c.uid,
      label: `${c.name} ${c.surname}`,
    })),
  ];

  if (isFetchingClients && clientPage > 1) {
    clientItems.push({
      key: 'loading',
      label: (
        <div style={{ textAlign: 'center', padding: '4px 0' }}>
          <Spin size="small" />
        </div>
      ),
    });
  }

  const sortItems = [
    { key: 'newest', label: 'Date (Newest)' },
    { key: 'oldest', label: 'Date (Oldest)' },
    { key: 'name', label: 'Name (A-Z)' },
  ];

  const uploadItems = [
    {
      key: 'add_photo',
      icon: <AddPhotoFiles style={{ fontSize: '16px' }} />,
      label: 'Add photo & files',
    },
    {
      key: 'take_photo',
      icon: <TakeAPhotos style={{ fontSize: '16px' }} />,
      label: 'Take a photo',
    },
  ];

  const getShowLabel = () => showItems.find((i) => i.key === showFilter)?.label || 'Active';
  const getClientLabel = () =>
    clientItems.find((i) => i.key === clientFilter)?.label || 'All Clients';
  const getSortLabel = () => sortItems.find((i) => i.key === sortFilter)?.label || 'Date (Newest)';

  const handleUploadMenuClick = (e: any) => {
    if (e.key === 'add_photo' || e.key === 'take_photo') {
      fileInputRef.current?.click();
    }
  };

  const allPrograms: ProgramData[] = useMemo(() => {
    if (!programsData || allClients.length === 0) return [];

    return programsData
      .filter((p) => {
        // Do not show programs belonging to deleted clients
        if (p.clientId && !allClients.some((c) => c.uid === p.clientId)) {
          return false;
        }
        return true;
      })
      .map((p) => {
        const client = allClients.find((c) => c.uid === p.clientId);
        const cName = client?.name || '';
        const cSurname = client?.surname || '';
        const clientName = client ? `${cName} ${cSurname}`.trim() : '';
        const initials = client ? `${cName.charAt(0)}${cSurname.charAt(0)}`.toUpperCase() : '';

        let durationNum = parseInt(p.duration || '1', 10);
        let daysPerWeek = p.datetimeList?.length
          ? Math.ceil(p.datetimeList.length / durationNum)
          : 0;

        const normalize = (str: string) => (str || '').replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

        const extractedImages: string[] = [];
        if (p.datetimeList) {
          for (const day of p.datetimeList) {
            const allDayMoves = [
              ...(day.warmUpMoves || []),
              ...(day.moves || []),
              ...(day.coolDownMoves || []),
            ];
            for (const move of allDayMoves) {
              const matchedExercise = allMovements?.find(
                (m) =>
                  normalize(m.id) === normalize(move.title) ||
                  normalize(m.en_display_name) === normalize(move.title),
              );
              const isFemale = isFemaleProfile(client || p);

              const imgUrl = isFemale
                ? matchedExercise?.image_female_url || matchedExercise?.image_male_url
                : matchedExercise?.image_male_url || matchedExercise?.image_female_url;

              if (matchedExercise) {
                extractedImages.push(imgUrl || '');
              }
              if (extractedImages.length >= 3) break;
            }
            if (extractedImages.length >= 3) break;
          }
        }

        const defaultImages = ['', '', ''];

        const draftProgress = p.draft?.progress || {};
        let nextStepSlug = 'energy-level';
        if (!draftProgress['energy_level'] || draftProgress['energy_level'].length === 0) {
          nextStepSlug = 'energy-level';
        } else if (
          !draftProgress['activity_level'] ||
          draftProgress['activity_level'].length === 0
        ) {
          nextStepSlug = 'non-gym-activity';
        } else if (!draftProgress['fitness_level'] || draftProgress['fitness_level'].length === 0) {
          nextStepSlug = 'fitness-level';
        } else if (
          !draftProgress['movement_insights'] ||
          draftProgress['movement_insights'].length < 5
        ) {
          nextStepSlug = 'movement-insights';
        } else if (
          !draftProgress['injuries_parts'] ||
          draftProgress['injuries_parts'].length === 0
        ) {
          nextStepSlug = 'injuries-limitations';
        } else if (
          !draftProgress['program_duration'] ||
          draftProgress['program_duration'].length === 0
        ) {
          nextStepSlug = 'program-duration';
        } else if (
          !draftProgress['how_often_train'] ||
          draftProgress['how_often_train'].length === 0
        ) {
          nextStepSlug = 'training-schedule';
        } else if (
          !draftProgress['workout_duration'] ||
          draftProgress['workout_duration'].length === 0
        ) {
          nextStepSlug = 'workout-duration';
        } else if (!draftProgress['training_goal'] || draftProgress['training_goal'].length === 0) {
          nextStepSlug = 'training-goal';
        }

        if (p.status === 'draft') {
          if (draftProgress['program_duration'] && draftProgress['program_duration'].length > 0) {
            durationNum = parseInt(draftProgress['program_duration'][0].value, 10);
          }
          if (draftProgress['how_often_train']) {
            daysPerWeek = draftProgress['how_often_train'].length;
          }
        }

        const isArchived = p.isActive === '0' || String(p.isActive) === '0';

        return {
          id: p.id,
          docPath:
            p.docPath ||
            (p.id && trainerId ? `trainer_app_users/${trainerId}/programme_plan/${p.id}` : ''),
          isSent: !!p.isSentToClient,
          clientId: p.clientId,
          generatorType: p.generatorType || (p as any)?.type || (p.isManually ? 'manual' : 'ai'),
          isManually: p.isManually,
          programmeName:
            p.status === 'draft' ? p.programmeName || 'Draft' : p.programmeName || 'Program',
          weeks: durationNum,
          daysPerWeek,
          tags: p.focus_area || 'General',
          thumbnails: [
            extractedImages[0] || defaultImages[0],
            extractedImages[1] || defaultImages[1],
            extractedImages[2] || defaultImages[2],
          ],
          nextStepSlug,
          assignedTo: {
            name: clientName,
            initials,
          },
          status: p.status === 'draft' ? 'draft' : p.clientId ? 'assigned' : 'unassigned',
          isActive: isArchived ? '0' : '1',
          createTime: p.createTime || new Date().toISOString(),
          date: (() => {
            const d = new Date(p.createTime || new Date().toISOString());
            const day = d.getDate();
            const month = d.toLocaleDateString('en-GB', { month: 'short' });
            const year = d.getFullYear();
            return `${day} ${month}, ${year}`;
          })(),
        };
      });
  }, [programsData, allClients, allMovements, trainerId]);

  const validActivePrograms = useMemo(() => {
    if (!activeProgramsData) return [];
    return activeProgramsData.filter((p) => {
      if (
        p.docPath &&
        (p.docPath.startsWith('mover_app_users/') || p.docPath.startsWith('flutter_user/'))
      ) {
        return false;
      }
      if (p.clientId && allClients.length > 0 && !allClients.some((c) => c.uid === p.clientId)) {
        return false;
      }
      return p.isActive === '1' || p.isActive !== '0';
    });
  }, [activeProgramsData, allClients]);

  const displayedPrograms = useMemo(() => {
    const filtered = allPrograms.filter((p) => {
      // Exclude programs fetched from client user collections (if they somehow appear in the payload)
      if (
        p.docPath &&
        (p.docPath.startsWith('mover_app_users/') || p.docPath.startsWith('flutter_user/'))
      )
        return false;

      if (showFilter === '1') {
        if (p.isActive === '0') return false;
      } else if (showFilter === '0') {
        if (p.isActive !== '0') return false;
      }
      if (clientFilter !== 'all' && p.clientId !== clientFilter) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (
          !p.programmeName.toLowerCase().includes(query) &&
          !p.assignedTo.name.toLowerCase().includes(query)
        ) {
          return false;
        }
      }
      return true;
    });

    return filtered.sort((a, b) => {
      if (sortFilter === 'newest') {
        return new Date(b.createTime).getTime() - new Date(a.createTime).getTime();
      } else if (sortFilter === 'oldest') {
        return new Date(a.createTime).getTime() - new Date(b.createTime).getTime();
      } else if (sortFilter === 'name') {
        return a.programmeName.localeCompare(b.programmeName);
      }
      return 0;
    });
  }, [allPrograms, showFilter, clientFilter, sortFilter, searchQuery]);

  return (
    <div className="dashboard-screen program-screen">
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept="image/*, .pdf, .doc, .docx"
      />
      <div className="dashboard-top-section desktop-only">
        <div className="dashboard-header">
          <h1>Programs</h1>
          <div className="header-actions">
            <Button
              variant="light"
              onClick={() => {
                // if (allPrograms.length >= 1) {
                //   navigate('/onboarding-trainer/dashboard');
                // } else {
                //   navigate('/onboarding-trainer/dashboard/program-generator/ai?action=new_program');
                // }
                navigate('/onboarding-trainer/dashboard');
              }}
            >
              <span style={{ marginRight: '8px', display: 'flex', alignItems: 'center' }}>
                {<AiIcon />}
              </span>
              {validActivePrograms.length === 0 && !isActiveProgramsLoading
                ? 'Create Your First Program'
                : 'Create Program'}
            </Button>
            <Dropdown
              menu={{ items: uploadItems, onClick: handleUploadMenuClick }}
              trigger={['click']}
              rootClassName="dark-theme-dropdown"
              placement="bottomRight"
            >
              <div style={{ display: 'inline-block', cursor: 'pointer' }}>
                <Button variant="outline" style={{ color: '#fff', pointerEvents: 'none' }}>
                  <span style={{ marginRight: '8px', display: 'flex', alignItems: 'center' }}>
                    {<UploadIcon />}
                  </span>
                  {'Upload a Program'}
                </Button>
              </div>
            </Dropdown>
          </div>
        </div>

        <div className="dashboard-filter-bar">
          <div className="filters-left">
            <Dropdown
              menu={{ items: showItems, onClick: (e) => setShowFilter(e.key) }}
              trigger={['click']}
              rootClassName="dark-theme-dropdown"
            >
              <div className="filter-item filter-show" style={{ cursor: 'pointer' }}>
                <span>
                  Show: <span className="value">{getShowLabel()}</span>
                </span>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </div>
            </Dropdown>
            <div className="divider"></div>
            <div className="search-input">
              {<SearchIcon />}
              <input
                type="text"
                placeholder="Find program.."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="clear-search-btn"
                  aria-label="Clear search"
                  style={{
                    background: 'transparent',
                    backgroundColor: 'transparent',
                    backgroundImage: 'none',
                    border: 'none',
                    padding: 0,
                    margin: 0,
                    outline: 'none',
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    borderRadius: 0,
                    boxShadow: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    color: '#838e9e',
                    width: 20,
                    height: 20,
                    minWidth: 'unset',
                    minHeight: 'unset',
                    flexShrink: 0,
                  }}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ display: 'block', margin: 0, pointerEvents: 'none' }}
                  >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              )}
            </div>
          </div>

          <div className="filters-right">
            <div className="filter-label">
              {<FilterIcon />}
              <span>{'Filters:'}</span>
            </div>
            <Dropdown
              menu={{ items: clientItems, onClick: (e) => setClientFilter(e.key) }}
              trigger={['click']}
              rootClassName="dark-theme-dropdown"
              popupRender={(menu) =>
                cloneElement(menu as React.ReactElement<any>, {
                  onScroll: (e: React.UIEvent<HTMLUListElement>) => {
                    const target = e.currentTarget;
                    if (
                      Math.abs(target.scrollHeight - target.clientHeight - target.scrollTop) <= 1
                    ) {
                      if (allClients.length < totalClientCount && !isFetchingClients) {
                        setClientPage((prev) => prev + 1);
                      }
                    }
                  },
                })
              }
            >
              <div className="filter-item filter-sort" style={{ cursor: 'pointer' }}>
                <span>
                  Client: <span className="value">{getClientLabel()}</span>
                </span>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </div>
            </Dropdown>
            <Dropdown
              menu={{ items: sortItems, onClick: (e) => setSortFilter(e.key) }}
              trigger={['click']}
              rootClassName="dark-theme-dropdown"
            >
              <div className="filter-item filter-sort" style={{ cursor: 'pointer' }}>
                <span>
                  Sort: <span className="value">{getSortLabel()}</span>
                </span>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </div>
            </Dropdown>
            <div className="view-toggles">
              <button
                className={`toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
                aria-label="Grid view"
              >
                <MenuIcon viewBox="0 0 24 24" />
              </button>
              <button
                className={`toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
                aria-label="List view"
              >
                <ListIcon viewBox="0 0 24 24" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="mobile-only">
        <div className="dashboard-top-section">
          <div className="dashboard-header">
            <h1>Programs</h1>
            <div className="header-actions">
              <Button variant="light" onClick={() => navigate('/onboarding-trainer/dashboard')}>
                <span style={{ marginRight: '8px', display: 'flex', alignItems: 'center' }}>
                  {<AiIcon />}
                </span>
                {'Create'}
              </Button>
              <Dropdown
                menu={{ items: uploadItems, onClick: handleUploadMenuClick }}
                trigger={['click']}
                rootClassName="dark-theme-dropdown"
                placement="bottomRight"
              >
                <div style={{ display: 'inline-block', cursor: 'pointer' }}>
                  <Button variant="outline" style={{ color: '#fff', pointerEvents: 'none' }}>
                    <span style={{ display: 'flex', alignItems: 'center' }}>{<UploadIcon />}</span>
                  </Button>
                </div>
              </Dropdown>
            </div>
          </div>

          <div className="dashboard-filter-bar">
            <div className="filter-label">
              {<FilterIcon />}
              <span>{'Filters:'}</span>
            </div>
            <div className="filter-row">
              <Dropdown
                menu={{ items: showItems, onClick: (e) => setShowFilter(e.key) }}
                trigger={['click']}
                rootClassName="dark-theme-dropdown"
              >
                <div className="filter-item filter-show" style={{ cursor: 'pointer' }}>
                  <span>
                    Show: <span className="value">{getShowLabel()}</span>
                  </span>
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </div>
              </Dropdown>
              <Dropdown
                menu={{ items: sortItems, onClick: (e) => setSortFilter(e.key) }}
                trigger={['click']}
                rootClassName="dark-theme-dropdown"
              >
                <div className="filter-item filter-sort" style={{ cursor: 'pointer' }}>
                  <span>
                    Sort: <span className="value">{getSortLabel()}</span>
                  </span>
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </div>
              </Dropdown>
            </div>
            <div className="filter-row">
              <Dropdown
                menu={{ items: clientItems, onClick: (e) => setClientFilter(e.key) }}
                trigger={['click']}
                rootClassName="dark-theme-dropdown"
                popupRender={(menu) =>
                  cloneElement(menu as React.ReactElement<any>, {
                    onScroll: (e: React.UIEvent<HTMLUListElement>) => {
                      const target = e.currentTarget;
                      if (
                        Math.abs(target.scrollHeight - target.clientHeight - target.scrollTop) <= 1
                      ) {
                        if (allClients.length < totalClientCount && !isFetchingClients) {
                          setClientPage((prev) => prev + 1);
                        }
                      }
                    },
                  })
                }
              >
                <div className="filter-item filter-sort" style={{ cursor: 'pointer' }}>
                  <span>
                    Client: <span className="value">{getClientLabel()}</span>
                  </span>
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </div>
              </Dropdown>
            </div>
          </div>
        </div>
        <div className="dashboard-search-section">
          <div className="filter-row">
            <div className="search-input">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
              <input
                type="text"
                placeholder="Find program.."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="clear-search-btn"
                  aria-label="Clear search"
                  style={{
                    background: 'transparent',
                    backgroundColor: 'transparent',
                    backgroundImage: 'none',
                    border: 'none',
                    padding: 0,
                    margin: 0,
                    outline: 'none',
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    borderRadius: 0,
                    boxShadow: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    color: '#838e9e',
                    width: 20,
                    height: 20,
                    minWidth: 'unset',
                    minHeight: 'unset',
                    flexShrink: 0,
                  }}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ display: 'block', margin: 0, pointerEvents: 'none' }}
                  >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              )}
            </div>
            <div className="view-toggles">
              <button
                className={`toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
                aria-label="Grid view"
              >
                <MenuIcon viewBox="0 0 24 24" />
              </button>
              <button
                className={`toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
                aria-label="List view"
              >
                <ListIcon viewBox="0 0 24 24" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {isProgramsLoading ? (
        <div className="loading-container" style={{ flex: 1 }}>
          <Spin indicator={<LoadingOutlined className="loading-icon" spin />} />
          <span className="loading-text">Loading programs</span>
        </div>
      ) : displayedPrograms.length === 0 ? (
        <div className="dashboard-empty-state">
          <div className="empty-icon">
            <ProgramIcon />
          </div>
          <h2>
            {searchQuery
              ? `No results found for "${searchQuery}"`
              : showFilter === '0'
                ? 'No archived programs yet'
                : 'No programs yet'}
          </h2>
          {!searchQuery && showFilter !== '0' && (
            <>
              <p>Create your first workout program</p>
              <Button variant="light" onClick={() => navigate('/onboarding-trainer/dashboard')}>
                <span style={{ marginRight: '8px', display: 'flex', alignItems: 'center' }}>
                  <AiIcon />
                </span>
                {validActivePrograms.length === 0 ? 'Create Your First Program' : 'Create Program'}
              </Button>
            </>
          )}
        </div>
      ) : (
        <div className={`programs-content ${viewMode}-layout`}>
          {displayedPrograms.map((program) => (
            <ProgramCard key={program.id} data={program} viewMode={viewMode} />
          ))}
        </div>
      )}
    </div>
  );
};
